/**
 * SplashScreen — Utilitário estático para controle da splash screen da nova área.
 *
 * Aguarda o evento 'load' da janela (DOM + todos os recursos) e as fontes
 * customizadas estarem prontas, depois aplica um breve delay antes de esconder.
 *
 * A abordagem anterior com PerformanceObserver/layout-shift travava em páginas
 * com conteúdo dinâmico (AJAX), pois cada re-render resetava o timer indefinidamente.
 */
class SplashScreen {
    static #HIDE_DELAY_MS = 300;

    /**
     * Exibe a splash screen.
     */
    static show() {
        document.body.classList.remove('splash-screen-hidden');
    }

    /**
     * Esconde a splash screen.
     */
    static hide() {
        document.body.classList.add('splash-screen-hidden');
    }

    /**
     * Aguarda a página estar pronta e então esconde a splash.
     *
     * Critérios (em paralelo):
     *  1. window 'load' — DOM + scripts + imagens + CSS carregados.
     *  2. document.fonts.ready — fontes customizadas renderizadas.
     * Depois: delay fixo para suavizar a transição.
     *
     * @param {number|null} delay - delay em ms após load+fonts (padrão: 300ms).
     * @returns {Promise<void>}
     */
    static async init(delay = null) {
        await Promise.all([
            SplashScreen.#waitForWindowLoad(),
            SplashScreen.#waitForFontsReady(),
        ]);

        await SplashScreen.#sleep(delay ?? SplashScreen.#HIDE_DELAY_MS);
        SplashScreen.hide();
    }

    // ─── Privados ─────────────────────────────────────────────────────────────

    static #waitForWindowLoad() {
        return new Promise((resolve) => {
            if (document.readyState === 'complete') return resolve();
            window.addEventListener('load', resolve, { once: true });
        });
    }

    static #waitForFontsReady() {
        return document.fonts?.ready ?? Promise.resolve();
    }

    static #sleep(ms) {
        return new Promise(resolve => setTimeout(resolve, ms));
    }
}

export default SplashScreen;
